import React, { FC } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { Divider, Stack } from '@mui/material';
import {
  useModelFilterActions,
  useModelFilters,
} from '@store/zustand/model-filters';
import { countSelected } from '@utils/cisCatalogue';
import {
  SCOPE_FILTER_UNASSIGNED,
  SCOPE_UNASSIGNED_LABEL,
  scopeDomainValueIds,
  ScopeTree,
  ScopeType,
} from '@utils/domains';
import { CATEGORY_LABELS } from '@views/Project/components/ModelFiltersPopover/categories';
import FilterNavRow from '@views/Project/components/ModelFiltersPopover/components/FilterNavRow';
import FilterPanelBackTitle from '@views/Project/components/ModelFiltersPopover/components/FilterPanelBackTitle';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';
import FilterSubtitle from '@views/Project/components/ModelFiltersPopover/components/FilterSubtitle';
import FilterValueRow from '@views/Project/components/ModelFiltersPopover/components/FilterValueRow';
import ScopeTypeSelect from '@views/Project/components/ModelFiltersPopover/components/ScopeTypeSelect';
import { useSetCategory } from '@views/Project/components/ModelFiltersPopover/useSetCategory';

export const SCOPE_DOMAINS_LABEL = 'Domains';

type Props = {
  tree: ScopeTree;
  scopeType: ScopeType;
  counts?: Record<string, number>;
  activeDomainId?: string;
  onScopeTypeChange: (scopeType: ScopeType) => void;
  onOpenDomain: (domainId: string) => void;
  onBack: () => void;
};

/**
 * The Scope category panel: the header, then Enterprise wide and Unassigned
 * as values, then — only while the project has Domains — a divider and a
 * "Domains" group of navigation rows, one per Domain in Project-settings
 * order.
 *
 * The header reads back control, title, a 24px vertical rule, then the
 * Scope type selector, as the design's header does, with "Clear all" at
 * the far right while Scope has a selection; Clear all empties every Scope
 * value and keeps the type. The selector reports the chosen type and the container
 * applies it, since the switch also reconciles the selection against the
 * canvas. A tick writes through `useSetCategory`, so it applies at once and
 * closes a drawer it would hide. A Domain row carries no checkbox and no
 * count, but a badge with the number of its values — itself and its
 * Sub-domains — currently selected; its click is reported, and the
 * container opens, swaps or closes the Domain's side panel from it, marking
 * the open row through `activeDomainId`. Counts are rendered only when the
 * container supplies them; a value missing from the map reads 0.
 */
const FilterScopePanel: FC<Props> = ({
  tree,
  scopeType,
  counts,
  activeDomainId,
  onScopeTypeChange,
  onOpenDomain,
  onBack,
}) => {
  const { domains: selected } = useModelFilters();
  const { clearCategory } = useModelFilterActions();
  const setCategory = useSetCategory();

  const peers = [
    ...(tree.wide ? [tree.wide] : []),
    { id: SCOPE_FILTER_UNASSIGNED, label: SCOPE_UNASSIGNED_LABEL },
  ];

  const toggle = (id: string) =>
    setCategory(
      'domains',
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );

  return (
    <FilterPanelLayout
      title={
        <Stack direction="row" alignItems="center" gap="12px" minWidth={0}>
          <FilterPanelBackTitle
            title={CATEGORY_LABELS.domains}
            onBack={onBack}
          />

          <Divider
            orientation="vertical"
            sx={{ height: 24, borderColor: 'grey.500' }}
          />

          <ScopeTypeSelect value={scopeType} onChange={onScopeTypeChange} />
        </Stack>
      }
      action={
        selected.length > 0 && (
          <UnderlineTextButton
            $disabled={false}
            onClick={() => clearCategory('domains')}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {peers.map(({ id, label }) => (
        <FilterValueRow
          key={id}
          label={label}
          checked={selected.includes(id)}
          count={counts ? counts[id] ?? 0 : undefined}
          onToggle={() => toggle(id)}
        />
      ))}

      {tree.domains.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'grey.500' }} />

          <FilterSubtitle>{SCOPE_DOMAINS_LABEL}</FilterSubtitle>

          {tree.domains.map((domain) => (
            <FilterNavRow
              key={domain.id}
              label={domain.label}
              badge={countSelected(scopeDomainValueIds(domain), selected)}
              selected={domain.id === activeDomainId}
              onClick={() => onOpenDomain(domain.id)}
            />
          ))}
        </>
      )}
    </FilterPanelLayout>
  );
};

export default FilterScopePanel;
